/**
 * The customer page's credit chip (docs/features/credit-control/06_ui_strategy.md
 * §2): amber "On stop" when the account is stopped, otherwise how the payer pays: "Card only"
 * (the default), "Card · Pro-forma", "Pro-forma only" or "Credit · {terms}".
 * A sub-account shows its payer's and says whom it is billed to. The chip
 * links to the customer's Settings, where the payment methods change.
 *
 * Self-loading by customer id: it asks for the composed credit read itself and
 * renders nothing while that loads or if it cannot (the header must not break
 * on a missing credit read). Mounted beside the segment badge.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CustomerCreditView } from "@shared-core/data/credit-control-types";
import { creditChip } from "@shared-core/domain/credit-control-presentation";
import { useCreditChangedListener } from "@shared-core/domain/creditEvents";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../primitives/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../primitives/tooltip";

export function CreditChip({ customerId }: { customerId: string }) {
  const adapter = useDataAdapter();
  const [view, setView] = useState<CustomerCreditView | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    adapter
      .getCustomerCredit(customerId)
      .then((credit) => {
        if (!cancelled) setView(credit);
      })
      .catch(() => {
        if (!cancelled) setView(null);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, customerId]);

  useEffect(() => {
    setView(null);
    return load();
  }, [load]);
  // A decision on held orders or a change to the account is reported, so the header agrees.
  useCreditChangedListener(load);

  if (!view) return null;
  const chip = creditChip(view);
  const badge = (
    <Badge variant={chip.variant === "on_stop" ? "warning" : "neutral"}>
      {chip.label}
    </Badge>
  );
  const trigger = (
    <Link
      to={paths.customerSettings({ customerId })}
      className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {badge}
    </Link>
  );
  if (!chip.tooltip) return trigger;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent>{chip.tooltip}</TooltipContent>
    </Tooltip>
  );
}
